import React, { useState, useEffect, useCallback, memo, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProductCard } from "../components/ui/ProductCard";
import { Spinner } from "../components/ui/Spinner";
import { Button } from "../components/ui/Button";
import { CategoryFilter } from "../components/CategoryFilter";
import { useApi } from "../context/ApiContext";
import { getResizedImageUrl, ImagePresets } from "../utils/imageUtils";
import { useCurrency } from "../context/CurrencyContext";
import { PageZone } from "../components/pageBuilder/PageZone";
import { useCart } from "../context/CartContext";
import { FaFilter, FaChevronDown, FaTimes } from "react-icons/fa";
import { SearchInput } from "../components/SearchInput";
import { useStoreLogoPlaceholder } from "../hooks/useStoreLogoPlaceholder";
import {
  calculateAvailableInventory,
  isOutOfStock,
  calculateProductCardPrice,
  calculateProductCardCompareAtPrice,
} from "../utils/inventoryUtils";

// Memoized SearchBar component (without search input - that's now separate)
interface SearchBarProps {
  sort: string;
  category: string;
  minPrice: string;
  maxPrice: string;
  tags: string[];
  isCustomizable: boolean;
  onSortChange: (value: string) => void;
  onFilterToggle: () => void;
}

const SearchBar = memo<SearchBarProps>(
  ({
    sort,
    category,
    minPrice,
    maxPrice,
    tags,
    isCustomizable,
    onSortChange,
    onFilterToggle,
  }) => {
    const { t } = useTranslation();

    return (
      <>
        <div className="relative">
          <select
            value={sort}
            onChange={(e) => onSortChange(e.target.value)}
            className="appearance-none px-4 py-2 pr-8 border border-store-secondary focus:outline-none focus:ring-2 focus:ring-store-secondary focus:border-store-secondary text-sm bg-white cursor-pointer hover:border-store-secondary transition-colors"
          >
            <option value="newest">{t("products.newest")}</option>
            <option value="oldest">{t("products.oldest")}</option>
            <option value="price-asc">{t("products.priceLowToHigh")}</option>
            <option value="price-desc">{t("products.priceHighToLow")}</option>
            <option value="name-asc">{t("products.nameAZ")}</option>
            <option value="name-desc">{t("products.nameZA")}</option>
          </select>
          <FaChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-store-text pointer-events-none text-xs" />
        </div>
        <button
          type="button"
          onClick={onFilterToggle}
          className="px-4 py-2 border border-store-secondary hover:border-store-secondary focus:outline-none focus:ring-2 focus:ring-store-secondary focus:border-store-secondary text-sm bg-white flex items-center gap-2 transition-colors"
        >
          <FaFilter className="text-store-primary" />
          <span className="text-store-primary">{t("products.filters")}</span>
          {(category ||
            minPrice ||
            maxPrice ||
            tags.length > 0 ||
            isCustomizable) && (
            <span className="bg-store-secondary text-white text-xs px-2 py-0.5">
              {
                [
                  category,
                  minPrice,
                  maxPrice,
                  tags.length,
                  isCustomizable,
                ].filter(Boolean).length
              }
            </span>
          )}
        </button>
      </>
    );
  },
);

SearchBar.displayName = "SearchBar";

export const ProductList: React.FC = () => {
  const { t } = useTranslation();
  const { api } = useApi();
  const { currency, location } = useCurrency();
  const { addToCart } = useCart();
  const storeLogoPlaceholder = useStoreLogoPlaceholder();
  const [searchParams, setSearchParams] = useSearchParams();

  const [products, setProducts] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [limit] = useState(24);
  const [debouncedSearch, setDebouncedSearch] = useState(
    searchParams.get("search") || "",
  );
  const [sort, setSort] = useState(searchParams.get("sort") || "newest");
  const [category, setCategory] = useState(searchParams.get("category") || "");
  const [minPrice, setMinPrice] = useState(searchParams.get("minPrice") || "");
  const [maxPrice, setMaxPrice] = useState(searchParams.get("maxPrice") || "");
  const [tags, setTags] = useState<string[]>(
    searchParams.get("tags") ? searchParams.get("tags")!.split(",") : [],
  );
  const [isCustomizable, setIsCustomizable] = useState(
    searchParams.get("isCustomizable") === "true",
  );
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  // Local filter states that don't trigger URL updates until sidebar closes
  const [localCategory, setLocalCategory] = useState(
    searchParams.get("category") || "",
  );
  const [localMinPrice, setLocalMinPrice] = useState(
    searchParams.get("minPrice") || "",
  );
  const [localMaxPrice, setLocalMaxPrice] = useState(
    searchParams.get("maxPrice") || "",
  );
  const [localTags, setLocalTags] = useState<string[]>(
    searchParams.get("tags") ? searchParams.get("tags")!.split(",") : [],
  );
  const [localIsCustomizable, setLocalIsCustomizable] = useState(
    searchParams.get("isCustomizable") === "true",
  );

  // Ref to track the last URL string we synced from (to avoid unnecessary syncs)
  const lastSyncedUrlRef = useRef<string>("");

  // Sync state with URL search params when they change (e.g., from category link clicks)
  useEffect(() => {
    // Create a string representation of current search params
    const currentUrlString = searchParams.toString();

    // Skip if we've already synced from this URL
    if (currentUrlString === lastSyncedUrlRef.current) {
      return;
    }

    // Update the ref
    lastSyncedUrlRef.current = currentUrlString;

    const urlCategory = searchParams.get("category") || "";
    const urlSort = searchParams.get("sort") || "newest";
    const urlMinPrice = searchParams.get("minPrice") || "";
    const urlMaxPrice = searchParams.get("maxPrice") || "";
    const urlTags = searchParams.get("tags")
      ? searchParams.get("tags")!.split(",")
      : [];
    const urlIsCustomizable = searchParams.get("isCustomizable") === "true";
    const urlSearch = searchParams.get("search") || "";

    // Only update state if values actually changed to avoid unnecessary re-renders
    if (urlCategory !== category) {
      setCategory(urlCategory);
    }
    if (urlSort !== sort) {
      setSort(urlSort);
    }
    if (urlMinPrice !== minPrice) {
      setMinPrice(urlMinPrice);
    }
    if (urlMaxPrice !== maxPrice) {
      setMaxPrice(urlMaxPrice);
    }
    const tagsStr = JSON.stringify(urlTags);
    const currentTagsStr = JSON.stringify(tags);
    if (tagsStr !== currentTagsStr) {
      setTags(urlTags);
    }
    if (urlIsCustomizable !== isCustomizable) {
      setIsCustomizable(urlIsCustomizable);
    }
    if (urlSearch !== debouncedSearch) {
      setDebouncedSearch(urlSearch);
    }

    // Also update local filter states to keep them in sync
    if (urlCategory !== localCategory) {
      setLocalCategory(urlCategory);
    }
    if (urlMinPrice !== localMinPrice) {
      setLocalMinPrice(urlMinPrice);
    }
    if (urlMaxPrice !== localMaxPrice) {
      setLocalMaxPrice(urlMaxPrice);
    }
    const localTagsStr = JSON.stringify(urlTags);
    const currentLocalTagsStr = JSON.stringify(localTags);
    if (localTagsStr !== currentLocalTagsStr) {
      setLocalTags(urlTags);
    }
    if (urlIsCustomizable !== localIsCustomizable) {
      setLocalIsCustomizable(urlIsCustomizable);
    }
  }, [
    searchParams,
    category,
    sort,
    minPrice,
    maxPrice,
    tags,
    isCustomizable,
    debouncedSearch,
    localCategory,
    localMinPrice,
    localMaxPrice,
    localTags,
    localIsCustomizable,
  ]);

  // Sync local filter states when sidebar opens
  useEffect(() => {
    if (isFilterSidebarOpen) {
      setLocalCategory(category);
      setLocalMinPrice(minPrice);
      setLocalMaxPrice(maxPrice);
      setLocalTags(tags);
      setLocalIsCustomizable(isCustomizable);
    }
  }, [isFilterSidebarOpen, category, minPrice, maxPrice, tags, isCustomizable]);

  const fetchProducts = useCallback(
    async (currentPage: number = page, isInitial: boolean = false) => {
      try {
        if (isInitial) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        const params = new URLSearchParams({
          page: currentPage.toString(),
          limit: limit.toString(),
          sort,
          ...(debouncedSearch && { search: debouncedSearch }),
          ...(category && { category }),
          ...(minPrice && { minPrice }),
          ...(maxPrice && { maxPrice }),
          ...(tags.length > 0 && { tags: tags.join(",") }),
          ...(isCustomizable && { isCustomizable: "true" }),
        });

        const response = await api.get(`/products?${params.toString()}`);
        const newProducts = response.data.products || [];
        const totalProducts = response.data.total || 0;

        if (isInitial) {
          setProducts(newProducts);
          setHasMore(newProducts.length < totalProducts);
        } else {
          setProducts((prev) => {
            const updatedProducts = [...prev, ...newProducts];
            setHasMore(updatedProducts.length < totalProducts);
            return updatedProducts;
          });
        }

        setTotal(totalProducts);
        setPage(currentPage);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [
      api,
      page,
      debouncedSearch,
      sort,
      category,
      minPrice,
      maxPrice,
      tags,
      isCustomizable,
      limit,
    ],
  );

  // Initial fetch and refetch when filters change
  useEffect(() => {
    setPage(1);
    fetchProducts(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    sort,
    category,
    minPrice,
    maxPrice,
    tags,
    isCustomizable,
  ]);

  const updateSearchParams = useCallback(
    (params: Record<string, string | undefined>) => {
      // Start with current search params to preserve existing filters
      const newParams = new URLSearchParams(searchParams);

      // Update only the params that are provided
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          // Remove param if value is explicitly undefined or empty
          newParams.delete(key);
        }
      });

      // Update the last synced URL ref to the new URL to prevent sync useEffect from running
      lastSyncedUrlRef.current = newParams.toString();

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Handler for when search is ready from SearchInput component
  const handleSearchReady = useCallback(
    (searchQuery: string) => {
      setDebouncedSearch(searchQuery);
      // Only update the search param, preserve all other existing params
      updateSearchParams({
        ...(searchQuery ? { search: searchQuery } : { search: undefined }),
      });
    },
    [updateSearchParams],
  );

  const handleSortChange = useCallback(
    (newSort: string) => {
      setSort(newSort);
      // Only update the sort param, preserve all other existing params
      updateSearchParams({
        sort: newSort,
      });
    },
    [updateSearchParams],
  );

  const handleAddToCart = async (productId: string) => {
    try {
      await addToCart(productId);
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  };

  const handleFilterToggle = useCallback(() => {
    setIsFilterSidebarOpen((prev) => {
      if (prev) {
        // Apply local filter changes when closing sidebar
        setCategory(localCategory);
        setMinPrice(localMinPrice);
        setMaxPrice(localMaxPrice);
        setTags(localTags);
        setIsCustomizable(localIsCustomizable);
        // Update URL params - preserve search, only update filter params
        updateSearchParams({
          category: localCategory || undefined,
          minPrice: localMinPrice || undefined,
          maxPrice: localMaxPrice || undefined,
          tags: localTags.length > 0 ? localTags.join(",") : undefined,
          isCustomizable: localIsCustomizable ? "true" : undefined,
        });
      }
      return !prev;
    });
  }, [
    localCategory,
    localMinPrice,
    localMaxPrice,
    localTags,
    localIsCustomizable,
    updateSearchParams,
  ]);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || loadingMore) return;
    fetchProducts(page + 1, false);
  }, [hasMore, loadingMore, page, fetchProducts]);

  return (
    <div className="w-full">
      {/* Compact Header Section */}
      <div className="w-full bg-white border-b border-store-accent-light sticky z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          {/* Breadcrumbs - Compact */}
          <div className="flex items-center gap-2 text-xs md:text-sm mb-4">
            <Link
              to="/"
              className="text-store-primary hover:text-store-secondary transition-colors"
            >
              {t("breadcrumb.home")}
            </Link>
            <span className="text-store-accent">/</span>
            <span className="text-store-text">{t("breadcrumb.products")}</span>
            {category && (
              <>
                <span className="text-store-accent">/</span>
                <span className="text-store-text capitalize">{category}</span>
              </>
            )}
          </div>

          {/* Search Input - Separate component that manages its own state */}
          <div className="flex items-center gap-3 mb-4">
            <SearchInput
              initialValue={searchParams.get("search") || ""}
              onSearchReady={handleSearchReady}
              debounceMs={500}
            />
            <SearchBar
              sort={sort}
              category={category}
              minPrice={minPrice}
              maxPrice={maxPrice}
              tags={tags}
              isCustomizable={isCustomizable}
              onSortChange={handleSortChange}
              onFilterToggle={handleFilterToggle}
            />
          </div>
        </div>
      </div>

      {/* Products Section - Full Width */}
      <div className="w-full bg-white py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Spinner size="lg" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="text-xl font-semibold text-store-primary mb-2">
                {t("products.noProductsFound")}
              </h3>
              <p className="text-store-text">
                {t("products.tryAdjustingFilters")}
              </p>
            </div>
          ) : (
            <>
              {/* Results Count - Compact */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-store-text">
                  {t("products.showing", {
                    start: 1,
                    end: products.length,
                    total: total,
                  })}
                </p>
              </div>

              {/* Products Grid - Optimized for space */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-6">
                {products.map((product) => {
                  // Calculate price with location and variant support
                  const locationId = location?.locationId;
                  const price = calculateProductCardPrice(product, locationId);
                  const originalPrice =
                    calculateProductCardCompareAtPrice(product);
                  const image =
                    product.images?.find((img: any) => img.isPrimary) ||
                    product.images?.[0];
                  const imageUrl = image
                    ? getResizedImageUrl(
                        image.url,
                        ImagePresets.productCard,
                        storeLogoPlaceholder,
                      )
                    : storeLogoPlaceholder;

                  // Calculate inventory for product list (no variant selection, with location-specific inventory if available)
                  const availableInventory = calculateAvailableInventory(
                    product,
                    undefined,
                    locationId,
                  );
                  const outOfStock = isOutOfStock(
                    product,
                    undefined,
                    locationId,
                  );

                  return (
                    <ProductCard
                      key={product._id}
                      id={product._id}
                      title={product.title}
                      price={price}
                      originalPrice={originalPrice}
                      image={imageUrl}
                      currency={currency}
                      description={product.description}
                      href={`/products/${product.handle || product._id}`}
                      onAddToCart={handleAddToCart}
                      isOutOfStock={outOfStock}
                      inventoryQuantity={availableInventory}
                      product={product}
                      locationId={locationId}
                    />
                  );
                })}
              </div>

              {/* Load More Button */}
              <div className="flex flex-col justify-center items-center gap-4 mt-8">
                {hasMore && (
                  <Button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    variant="secondary"
                    size="md"
                    loading={loadingMore}
                  >
                    {t("products.loadMore") || "Load More"}
                  </Button>
                )}
                {!hasMore && products.length > 0 && (
                  <p className="text-sm text-store-text">
                    {t("products.allProductsLoaded")}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Filter Sidebar */}
      <div
        className={`fixed inset-0 z-50 transition-opacity duration-300 ${
          isFilterSidebarOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={() => {
            // Apply local filter changes when closing sidebar via overlay
            setCategory(localCategory);
            setMinPrice(localMinPrice);
            setMaxPrice(localMaxPrice);
            setTags(localTags);
            setIsCustomizable(localIsCustomizable);

            // Update URL params - preserve search, only update filter params
            updateSearchParams({
              category: localCategory || undefined,
              minPrice: localMinPrice || undefined,
              maxPrice: localMaxPrice || undefined,
              tags: localTags.length > 0 ? localTags.join(",") : undefined,
              isCustomizable: localIsCustomizable ? "true" : undefined,
            });

            setIsFilterSidebarOpen(false);
          }}
        />
        {/* Sidebar */}
        <div
          className={`absolute top-0 right-0 h-full w-full max-w-md bg-white transform transition-transform duration-300 ease-in-out overflow-y-auto ${
            isFilterSidebarOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-store-primary">
                {t("products.filters")}
              </h2>
              <button
                onClick={() => {
                  // Apply local filter changes when closing sidebar
                  setCategory(localCategory);
                  setMinPrice(localMinPrice);
                  setMaxPrice(localMaxPrice);
                  setTags(localTags);
                  setIsCustomizable(localIsCustomizable);

                  // Update URL params - preserve search, only update filter params
                  updateSearchParams({
                    category: localCategory || undefined,
                    minPrice: localMinPrice || undefined,
                    maxPrice: localMaxPrice || undefined,
                    tags:
                      localTags.length > 0 ? localTags.join(",") : undefined,
                    isCustomizable: localIsCustomizable ? "true" : undefined,
                  });

                  setIsFilterSidebarOpen(false);
                }}
                className="text-store-text hover:text-store-primary transition-colors"
              >
                <FaTimes className="text-lg" />
              </button>
            </div>

            {/* Category Filter */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-store-primary mb-3">
                {t("products.category")}
              </h3>
              <CategoryFilter
                selectedCategory={localCategory}
                onCategoryChange={(cat) => {
                  setLocalCategory(cat || "");
                }}
                variant="vertical"
              />
            </div>

            {/* Price Range */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-store-primary mb-3">
                {t("products.priceRange")}
              </h3>
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-store-text mb-1">
                    {t("products.minPrice")}
                  </label>
                  <input
                    type="number"
                    value={localMinPrice}
                    onChange={(e) => setLocalMinPrice(e.target.value)}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-store-secondary focus:outline-none focus:ring-2 focus:ring-store-secondary focus:border-store-secondary text-sm"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-store-text mb-1">
                    {t("products.maxPrice")}
                  </label>
                  <input
                    type="number"
                    value={localMaxPrice}
                    onChange={(e) => setLocalMaxPrice(e.target.value)}
                    placeholder="∞"
                    className="w-full px-3 py-2 border border-store-secondary focus:outline-none focus:ring-2 focus:ring-store-secondary focus:border-store-secondary text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Customizable Filter */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-store-primary mb-3">
                {t("products.options")}
              </h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={localIsCustomizable}
                  onChange={(e) => {
                    setLocalIsCustomizable(e.target.checked);
                  }}
                  className="w-4 h-4 text-store-secondary border-store-secondary rounded focus:ring-store-secondary focus:ring-2"
                />
                <span className="text-sm text-store-primary">
                  {t("products.customizableOnly")}
                </span>
              </label>
            </div>

            {/* Tags Filter - Simple text input for now */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-store-primary mb-3">
                {t("products.tags")}
              </h3>
              <input
                type="text"
                value={localTags.join(", ")}
                onChange={(e) => {
                  const newTags = e.target.value
                    .split(",")
                    .map((tag) => tag.trim())
                    .filter(Boolean);
                  setLocalTags(newTags);
                }}
                placeholder={t("products.tagsPlaceholder")}
                className="w-full px-3 py-2 border border-store-secondary focus:outline-none focus:ring-2 focus:ring-store-secondary focus:border-store-secondary text-sm"
              />
              <p className="text-xs text-store-text mt-1">
                {t("products.tagsHint")}
              </p>
            </div>

            {/* Clear Filters Button */}
            {(localCategory ||
              localMinPrice ||
              localMaxPrice ||
              localTags.length > 0 ||
              localIsCustomizable) && (
              <button
                onClick={() => {
                  setLocalCategory("");
                  setLocalMinPrice("");
                  setLocalMaxPrice("");
                  setLocalTags([]);
                  setLocalIsCustomizable(false);
                }}
                className="w-full px-4 py-2 border border-store-secondary hover:bg-store-background text-store-primary text-sm font-medium transition-colors"
              >
                {t("products.clearFilters")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Custom Sidebar Zone */}
      <PageZone zoneName="sidebar" pageType="product-list" />

      {/* Custom Footer Zone */}
      <PageZone zoneName="footer" pageType="product-list" />
    </div>
  );
};

export default ProductList;
